import React, { useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import styled from 'styled-components';
import { Points } from '@styles';
import { deviceDetected } from 'planoplan-ui-kit/core/device-detected';
import { useMediaQuery } from 'planoplan-ui-kit/core/hooks/useMediaQuery';
import { Colors } from 'planoplan-ui-kit/core/colors';
import { Mixins } from 'planoplan-ui-kit/core/mixins';
import { useTranslations } from '@libs/translations';
import { SectionNumber } from '@components/Section-Number';
import { Separator } from '@components/Separator';
import { DownloadButton } from './organism/download-button';
import { Button } from 'planoplan-ui-kit/core/button';

export const VirtualTour = () => {
  const translations = useTranslations();
  const isMobileDevice = deviceDetected.isMobile;
  const isTabletPortrait = useMediaQuery(Points.tablet_landscape, { useSSR: true });
  const isDesktopWide = useMediaQuery(Points.laptop_wide, { useSSR: true });
  const [visibleVr, setVisibleVr] = useState(false);
  const [endVisibleVr, setEndVisibleVr] = useState(false); // для анимации
  const [isNearViewport, setIsNearViewport] = useState(false);
  const columnIframeRef = useRef(null);
  const { locale } = useSelector((state) => state.settings);

  const tourUrlRu = 'https://planoplan.com/ru/preview/7aa889b99f0985391c3754208a54c14c/?tour=1&full=1';
  const tourUrlEn = 'https://planoplan.com/en/preview/0183cf9c6c38e141d39a07b440e6f6bc/?tour=1&full=1';

  const tourUrl = locale === 'ru' ? tourUrlRu : tourUrlEn;

  // Монтируем iframe только кода пользователь доскраливает до блока
  useEffect(() => {
    if (isNearViewport || !columnIframeRef.current) return;

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) {
          setIsNearViewport(true);
          observer.disconnect();
        }
      },
      { rootMargin: '400px 0px' }
    );

    observer.observe(columnIframeRef.current);

    return () => observer.disconnect();
  }, [isNearViewport]);

  const handleClickTour = () => {
    setVisibleVr(true);

    setTimeout(() => setEndVisibleVr(true), 300);
  };
  const openTour = () => {
    window.open(tourUrl, '_blank');
  };

  const handleClickTourOut = () => {
    setEndVisibleVr(false);
    setTimeout(() => setVisibleVr(false), 300);
  };

  return (
    <View id="virtual-tour">
      <Container>
        <Content>
          <ColumnInfo>
            <SectionNumber number={''} text={translations.t('site.home.tour.name')} textColor={Colors.white} />
            <Title>{translations.t('site.home.tour.title')}</Title>
            <Description>{translations.t('site.home.tour.description')}</Description>

            <Separator />
            <Text dangerouslySetInnerHTML={{ __html: translations.t('site.home.tour.text') }} />

            <SectionActions>
              <Action>
                <Button
                  theme="neutral_secondary"
                  data-gtm="button_click"
                  onClick={openTour}
                  size={isDesktopWide ? 'L' : isTabletPortrait ? 'M' : 'S'}>
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <circle cx="12" cy="12" r="3.5" stroke="black" />
                    <path d="M2 12C8 3 16 3 22 12C16 21 8 21 2 12Z" stroke="black" />
                  </svg>
                  {translations.t('site.home.tour.viewTour')}
                </Button>
              </Action>
              {!isMobileDevice && (
                <Action>
                  <DownloadButton />
                </Action>
              )}
            </SectionActions>
          </ColumnInfo>
          <ColumnIframe ref={columnIframeRef}>
            {!endVisibleVr && (
              <Start data-gtm="button_click" onClick={handleClickTour} visible={!visibleVr}>
                <StartPlay>
                  <svg width="16" height="19" viewBox="0 0 16 19" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path
                      d="M15.5493 8.31886C16.1502 8.66581 16.1502 9.53319 15.5493 9.88015L1.35211 18.0769C0.751174 18.4238 -3.03315e-08 17.9902 0 17.2962L7.16587e-07 0.902754C7.46919e-07 0.208849 0.751175 -0.224842 1.35211 0.122111L15.5493 8.31886Z"
                      fill="#F9F4F1"
                    />
                  </svg>
                </StartPlay>
                <StartText>{translations.t('site.home.tour.start')}</StartText>
              </Start>
            )}
            {endVisibleVr && (
              <CloseTourBtn data-gtm="button_click" onClick={handleClickTourOut}>
                {translations.t('site.home.tour.end')}
              </CloseTourBtn>
            )}
            <IframeWrap visibleOverlay={!visibleVr} hasOverlay={!endVisibleVr}>
              {isNearViewport && <Iframe src={tourUrl} title={translations.t('site.home.tour.iframeTitle')} />}
            </IframeWrap>
          </ColumnIframe>
        </Content>
      </Container>
    </View>
  );
};

const View = styled.section`
  height: auto;
  position: relative;
  overflow: hidden;
  user-select: none;
  -webkit-user-select: none;
  padding-top: 16px;
  padding-bottom: 30px;
  background-color: ${Colors.main_black};
  color: ${Colors.white};
  position: relative;

  @media ${Points.tablet_landscape} {
    padding-bottom: 52px;
    padding-top: 30px;
  }

  @media ${Points.laptop_wide} {
    padding-top: 100px;
    padding-bottom: 100px;
  }

  @media ${Points.fullhd} {
    padding-bottom: 130px;
  }
`;
const Container = styled.div`
  padding-left: 16px;
  padding-right: 16px;
  width: 100%;
  display: flex;

  @media ${Points.tablet_landscape} {
    padding: 0;
  }

  @media ${Points.laptop_wide} {
    width: 100%;
  }
`;
const Content = styled.div`
  width: 100%;
  margin: 0 auto;
  display: flex;
  flex-direction: column-reverse;

  @media ${Points.tablet_landscape} {
    align-items: flex-start;
    flex-direction: row;
    width: 100%;
    max-width: 1024px;
    padding: 0 28px;
  }

  @media ${Points.laptop_wide} {
    width: 100%;
    max-width: 1600px;
    padding: 0 80px;
  }

  @media ${Points.fullhd} {
    padding: 0;
  }
`;
const ColumnInfo = styled.div`
  width: 100%;
  position: relative;
  z-index: 2;

  @media ${Points.tablet_landscape} {
    width: 430px;
    flex-shrink: 0;
    flex-grow: 0;
    margin-top: 10px;
  }

  @media ${Points.laptop_wide} {
    flex-basis: 520px;
    margin-top: 10px;
  }
  @media ${Points.fullhd} {
    margin-top: 20px;
  }
`;

const Title = styled.div`
  font-size: 26px;
  line-height: 1;
  margin-bottom: 15px;
  font-weight: 400;
  white-space: pre-line;

  @media ${Points.tablet_portrait} {
    text-align: center;
  }

  @media ${Points.tablet_landscape} {
    text-align: left;
    font-size: 34px;
  }
  @media ${Points.laptop_wide} {
    font-size: 54px;
    padding-right: 90px;
  }
`;
const Description = styled.div`
  font-size: 12px;
  line-height: 1.1;
  padding-right: 1px;

  @media ${Points.tablet_portrait} {
    text-align: center;
  }

  @media ${Points.tablet_landscape} {
    text-align: left;
  }

  @media ${Points.laptop_wide} {
    font-size: 14px;
  }
`;
const Text = styled.div`
  font-size: 18px;
  line-height: 1.3;
  margin-bottom: 30px;

  p {
    margin-bottom: 15px;

    @media ${Points.laptop_wide} {
      margin-bottom: 20px;
    }
  }

  @media ${Points.laptop_wide} {
    margin-bottom: 40px;
  }
`;
const SectionActions = styled.div`
  @media ${Points.tablet_portrait} {
    max-width: 280px;
    margin: 0 auto;
  }

  @media ${Points.tablet_landscape} {
    max-width: 380px;
    margin: 0;
  }
`;
const Action = styled.div`
  &:not(:last-child) {
    margin-bottom: 20px;
  }

  svg {
    margin-right: 10px;
  }
`;
const ColumnIframe = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  flex-grow: 1;
  width: 100%;
  height: 560px;
  padding: 16px 16px 0 16px;
  margin-bottom: 30px;
  position: relative;

  @media ${Points.tablet_landscape} {
    height: 100%;
    margin-bottom: 0;
    position: static;
  }
`;
const StartPlay = styled.div`
  ${Mixins.transition('transform')};
  width: 60px;
  height: 60px;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.2);
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  padding-top: 1px;
  padding-left: 3px;
  margin-bottom: 10px;
`;
const StartText = styled.div`
  text-align: center;
  max-width: 80%;
  font-size: 16px;
  line-height: 1.3;
`;
const Start = styled.div`
  ${Mixins.transition('opacity')};
  width: 200px;
  height: 200px;
  border-radius: 50%;
  background: rgba(0, 0, 0, 0.2);
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  position: relative;
  z-index: 2;
  cursor: pointer;
  opacity: ${({ visible }) => (visible ? 1 : 0)};

  &:hover {
    ${StartPlay} {
      transform: scale(1.1);
    }
  }

  &:active {
    ${StartPlay} {
      transform: scale(0.9);
    }
  }
`;
const Iframe = styled.iframe`
  width: 100%;
  height: 100%;
  border: none;
`;
const IframeWrap = styled.div`
  padding: 1px;
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;

  &:after {
    ${Mixins.transition('opacity')};
    content: '';
    width: 100%;
    height: 100%;
    position: absolute;
    top: 0;
    left: 0;
    z-index: 1;
    background: ${({ visibleOverlay }) =>
      visibleOverlay && `linear-gradient(90.14deg, rgba(0, 0, 0, 1) -6.08%, rgba(0, 0, 0, 0) 99.85%)`};
    opacity: ${({ visibleOverlay }) => (visibleOverlay ? '1' : '0.7')};
    pointer-events: ${({ hasOverlay }) => (hasOverlay ? 'auto' : 'none')};

    @media ${Points.tablet_landscape} {
      background: linear-gradient(90.14deg, rgba(0, 0, 0, 1) -6.08%, rgba(0, 0, 0, 0) 99.85%);
    }
  }

  ${Iframe} {
    pointer-events: ${({ hasOverlay }) => (hasOverlay ? 'none' : 'auto')};
  }

  @media ${Points.tablet_landscape} {
    padding: 0;
  }
`;

const CloseTourBtn = styled.button`
  display: none;

  @media ${Points.tablet_landscape} {
    position: absolute;
    z-index: 2;
    top: 40px;
    right: calc(100vw / 2 + 28px - 1024px / 2);
    cursor: pointer;
    display: block;
    padding: 13px 20px 12px;
    background: rgba(0, 0, 0, 0.2);
    border: none;
    border-radius: 1000px;
    color: ${Colors.white};
    font-weight: 400;
    font-size: 16px;
    line-height: 130%;
    letter-spacing: 0%;
    text-align: center;
  }

  @media ${Points.laptop_wide} {
    top: 110px;
    right: calc(100vw / 2 - 1440px / 2);
  }

  @media ${Points.fullhd} {
    top: 120px;
    right: calc(100vw / 2 + 160px - 1920px / 2);
  }

  &:hover {
    background: rgba(0, 0, 0, 0.25);
  }
`;
